import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, X, ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { GALLERY_FILTERS } from '@/lib/site';
import { useInspirations } from '@/context/InspirationContext';
import { useSite } from '@/context/SiteContext';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';
import { resolveImageUrl as resolveSrc } from '@/lib/resolveImageUrl';

export default function Galleria() {
    const { t } = useSite();
    useSeo({
        title: `${SITE_NAME} — Galleria`,
        description: "Galleria delle creazioni Floreal: matrimoni, bouquet, eventi, workshop, piante e giardini. Trova l'ispirazione per il tuo progetto.",
    });

    const [items, setItems] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const initialCat = searchParams.get('cat') || 'all';
    const [filter, setFilter] = useState(initialCat);
    const [loading, setLoading] = useState(true);
    const [activeIdx, setActiveIdx] = useState(null);
    const { isLiked, toggleLike, items: liked } = useInspirations();

    // Sync filter -> URL (?cat=...)
    useEffect(() => {
        const current = searchParams.get('cat') || 'all';
        if (current !== filter) {
            const next = new URLSearchParams(searchParams);
            if (filter === 'all') next.delete('cat'); else next.set('cat', filter);
            setSearchParams(next, { replace: true });
        }
    }, [filter]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const filterDef = GALLERY_FILTERS.find((f) => f.id === filter);
        const multiCats = filterDef?.categories;
        const fetchPromise = multiCats
            ? api.get('/gallery').then((res) => ({
                data: (res.data || []).filter((it) => multiCats.includes(it.category)),
            }))
            : api.get('/gallery', { params: filter === 'all' ? {} : { category: filter } });
        fetchPromise
            .then((res) => { if (!cancelled) setItems(res.data || []); })
            .catch((e) => {
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('[Galleria] Failed to load gallery items:', e);
                }
                toast.error('Impossibile caricare la galleria');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [filter]);

    const filtered = useMemo(() => items, [items]);

    const active = activeIdx !== null ? filtered[activeIdx] : null;

    const onPrev = useCallback(() => setActiveIdx((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length)), [filtered.length]);
    const onNext = useCallback(() => setActiveIdx((i) => (i === null ? null : (i + 1) % filtered.length)), [filtered.length]);

    useEffect(() => {
        const onKey = (e) => {
            if (activeIdx === null) return;
            if (e.key === 'Escape') setActiveIdx(null);
            if (e.key === 'ArrowLeft') onPrev();
            if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [activeIdx, onPrev, onNext]);

    const handleLike = (item, e) => {
        if (e) e.stopPropagation();
        const wasLiked = isLiked(item.id);
        toggleLike(item);
        toast.success(wasLiked ? 'Ispirazione rimossa' : 'Aggiunto alle ispirazioni', {
            description: wasLiked ? undefined : 'La trovi in “Prenota un appuntamento”, già pronta.',
        });
    };

    return (
        <>
            <section className="pt-32 sm:pt-40 pb-6">
                <div className="container-atelier max-w-3xl">
                    <p className="eyebrow mb-4">{t('galleria.hero.eyebrow', 'Galleria')}</p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight whitespace-pre-line">
                        {t('galleria.hero.title', 'Il nostro lavoro,\nin immagini.')}
                    </h1>
                    <p className="mt-5 text-muted-foreground leading-relaxed text-base sm:text-lg">
                        {t('galleria.hero.description', 'Sfoglia le composizioni, gli allestimenti e i workshop. Salva le immagini che ti emozionano: le ritroverai pronte nella richiesta di appuntamento.')}
                    </p>
                </div>
            </section>

            <Section className="pt-6 sm:pt-8">
                {/* Filters */}
                <div className="flex flex-wrap gap-2 mb-8">
                    {GALLERY_FILTERS.map((f) => (
                        <button
                            key={f.id}
                            data-testid={f.id === 'all' ? TEST_IDS.galleryFilterAll : `gallery-filter-${f.id}`}
                            onClick={() => setFilter(f.id)}
                            className={cn(
                                'h-9 px-4 text-sm rounded-full border transition-colors',
                                filter === f.id
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'bg-card border-border hover:bg-secondary',
                            )}
                        >
                            {f.label}
                        </button>
                    ))}
                    {liked.length > 0 && (
                        <Link
                            to="/prenota"
                            className="ml-auto inline-flex items-center gap-2 h-9 px-4 text-sm rounded-full border border-foreground/15 bg-atelier-blush/50 hover:bg-atelier-blush text-foreground transition-colors"
                        >
                            <Heart className="h-3.5 w-3.5 fill-current" /> {liked.length} ispirazion{liked.length === 1 ? 'e' : 'i'} — Prenota
                        </Link>
                    )}
                </div>

                {/* Masonry */}
                {loading ? (
                    <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
                        {Array.from({ length: 9 }).map((_, i) => (
                            <div key={`gallery-skeleton-${i}`} className="mb-4 break-inside-avoid rounded-md bg-muted animate-pulse" style={{ height: `${260 + (i % 3) * 80}px` }} />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16">
                        <ImageOff className="h-8 w-8 mx-auto text-muted-foreground" />
                        <p className="mt-3 text-muted-foreground">{t('galleria.empty', "Nessuna immagine per questo filtro. Prova un'altra atmosfera.")}</p>
                    </div>
                ) : (
                    <div data-testid={TEST_IDS.galleryGrid} className="columns-1 sm:columns-2 lg:columns-3 gap-4">
                        {filtered.map((it, idx) => (
                            <motion.figure
                                key={it.id}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-40px' }}
                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                className="mb-4 break-inside-avoid group relative cursor-zoom-in rounded-md overflow-hidden border border-border bg-card"
                                onClick={() => setActiveIdx(idx)}
                                data-testid={TEST_IDS.galleryItem}
                            >
                                <img
                                    src={resolveSrc(it.image_url)}
                                    alt={it.title}
                                    loading="lazy"
                                    className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                                />
                                <button
                                    onClick={(e) => handleLike(it, e)}
                                    data-testid={TEST_IDS.galleryLikeButton}
                                    aria-label={isLiked(it.id) ? 'Rimuovi dalle ispirazioni' : 'Mi piace questo stile'}
                                    className={cn(
                                        'absolute top-2 right-2 h-9 w-9 rounded-full backdrop-blur-md flex items-center justify-center transition-colors',
                                        isLiked(it.id)
                                            ? 'bg-atelier-blush text-foreground'
                                            : 'bg-background/70 text-foreground/70 hover:bg-background hover:text-foreground',
                                    )}
                                >
                                    <Heart className={cn('h-4 w-4', isLiked(it.id) && 'fill-current')} />
                                </button>
                                <figcaption className="absolute left-0 right-0 bottom-0 px-4 py-3 bg-gradient-to-t from-background/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <p className="text-sm font-medium">{it.title}</p>
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>
                )}
            </Section>

            {/* Lightbox */}
            <Dialog open={activeIdx !== null} onOpenChange={(o) => !o && setActiveIdx(null)}>
                <DialogContent
                    data-testid={TEST_IDS.galleryLightbox}
                    className="max-w-6xl w-[96vw] sm:w-[92vw] p-0 bg-background border-border overflow-hidden gap-0 [&>button.absolute]:hidden"
                >
                    {active && (
                        <div className="relative">
                            <img
                                src={resolveSrc(active.image_url)}
                                alt={active.title}
                                className="w-full max-h-[80vh] object-contain bg-atelier-cream"
                            />
                            <div className="absolute top-3 right-3 flex gap-2 z-10">
                                <button
                                    onClick={() => handleLike(active)}
                                    data-testid={TEST_IDS.galleryLightboxLikeButton}
                                    className={cn(
                                        'h-10 px-3 rounded-full backdrop-blur-md text-sm flex items-center gap-2 transition-colors',
                                        isLiked(active.id)
                                            ? 'bg-atelier-blush text-foreground'
                                            : 'bg-background/85 hover:bg-background text-foreground',
                                    )}
                                >
                                    <Heart className={cn('h-4 w-4', isLiked(active.id) && 'fill-current')} />
                                    {isLiked(active.id) ? 'Salvato' : 'Mi piace questo stile'}
                                </button>
                                <button
                                    onClick={() => setActiveIdx(null)}
                                    data-testid={TEST_IDS.galleryLightboxClose}
                                    className="h-10 w-10 rounded-full bg-background/85 hover:bg-background flex items-center justify-center"
                                    aria-label="Chiudi"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <button
                                onClick={onPrev}
                                aria-label="Precedente"
                                className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-background/85 hover:bg-background items-center justify-center"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                onClick={onNext}
                                aria-label="Successivo"
                                className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-background/85 hover:bg-background items-center justify-center"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                            <div className="px-5 py-4 border-t border-border flex items-center justify-between gap-3">
                                <div>
                                    <p className="font-serif text-xl">{active.title}</p>
                                    <Badge variant="outline" className="mt-1 text-xs border-border text-muted-foreground">{active.category}</Badge>
                                </div>
                                <Button asChild size="sm" variant="outline" className="hidden sm:inline-flex">
                                    <Link to="/prenota">Vai alla prenotazione</Link>
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
